import {TCountry, TRegion, TState} from "../types";
import {getCityIdsFromState, getStateIdsFromRegion} from "./idHelpers";

/**
 * Check if a country is selected
 */
export const isCountrySelected = (countryId: number, selectedLocation: number[]): boolean =>
	selectedLocation.map(Number).includes(Number(countryId));

/**
 * Check if a region is selected
 * Region is selected if explicitly in selectedCountryRegions OR parent country is selected
 */
export const isRegionSelected = (
	regionId: number,
	countryId: number,
	selectedCountryRegions: number[] = [],
	selectedLocation: number[] = [],
): boolean => {
	if (selectedCountryRegions.map(Number).includes(Number(regionId))) return true;
	if (selectedLocation.map(Number).includes(Number(countryId))) return true;

	return false;
};

/**
 * Check if a state is selected
 * State is selected if explicitly in selectedRegionStates OR parent region/country is selected
 */
export const isStateSelected = (
	stateId: number,
	regionId: number,
	countryId: number,
	selectedRegionStates: number[] = [],
	selectedCountryRegions: number[] = [],
	selectedLocation: number[] = [],
): boolean => {
	if (selectedRegionStates.map(Number).includes(Number(stateId))) return true;
	if (selectedCountryRegions.map(Number).includes(Number(regionId))) return true;
	if (selectedLocation.map(Number).includes(Number(countryId))) return true;

	return false;
};

/**
 * Check if a city is selected
 * City is selected if explicitly in selectedStateCities OR parent state/region/country is selected
 */
export const isCitySelected = (
	cityId: number,
	stateId: number,
	regionId: number,
	countryId: number,
	selectedStateCities: number[] = [],
	selectedRegionStates: number[] = [],
	selectedCountryRegions: number[] = [],
	selectedLocation: number[] = [],
): boolean => {
	if (selectedStateCities.map(Number).includes(Number(cityId))) return true;
	if (selectedRegionStates.map(Number).includes(Number(stateId))) return true;
	if (selectedCountryRegions.map(Number).includes(Number(regionId))) return true;
	if (selectedLocation.map(Number).includes(Number(countryId))) return true;

	return false;
};

/**
 * Check if a region is partially selected (some but not all children selected)
 * A region is partially selected if:
 * - Some (but not all) of its states are selected, OR
 * - Any of its states have some (but not all) cities selected
 */
export const isRegionPartiallySelected = (
	region: TRegion,
	country: TCountry,
	selectedCountryRegions: number[] = [],
	selectedRegionStates: number[] = [],
	selectedStateCities: number[] = [],
	selectedLocation: number[] = [],
): boolean => {
	const numCountries = selectedLocation.map(Number);
	const numRegions = selectedCountryRegions.map(Number);
	const numStates = selectedRegionStates.map(Number);
	const numCities = selectedStateCities.map(Number);

	// If country is selected => region cannot be partially selected (it's fully selected)
	if (numCountries.includes(Number(country.id))) return false;
	// If region explicitly selected => fully selected (not partial)
	if (numRegions.includes(Number(region.id))) return false;

	const stateIds = getStateIdsFromRegion(region);
	if (!stateIds || stateIds.length === 0) {
		return false;
	}

	let selectedStateCount = 0;
	let hasPartiallySelectedState = false;

	for (const state of region.states || []) {
		const isThisStateSelected = isStateSelected(
			state.id,
			region.id,
			country.id,
			numStates,
			numRegions,
			numCountries,
		);

		if (isThisStateSelected) {
			selectedStateCount++;
		} else {
			// Check if this state has any cities selected (making it partially selected)
			const cityIds = getCityIdsFromState(state);
			if (cityIds && cityIds.length > 0) {
				const hasSelectedCity = cityIds.some((cId) => numCities.includes(Number(cId)));
				if (hasSelectedCity) {
					hasPartiallySelectedState = true;
				}
			}
		}
	}

	// Region is partial if it's not fully selected (checked at start)
	// AND it has any selected states OR any partially selected state
	return (
		(selectedStateCount > 0 && selectedStateCount < stateIds.length) ||
		hasPartiallySelectedState
	);
};

/**
 * Check if a state is partially selected (some but not all cities selected)
 */
export const isStatePartiallySelected = (
	state: TState,
	region: TRegion,
	country: TCountry,
	selectedStateCities: number[] = [],
	selectedRegionStates: number[] = [],
	selectedCountryRegions: number[] = [],
	selectedLocation: number[] = [],
): boolean => {
	const numCountries = selectedLocation.map(Number);
	const numRegions = selectedCountryRegions.map(Number);
	const numStates = selectedRegionStates.map(Number);
	const numCities = selectedStateCities.map(Number);

	// If region or country selected => state is fully selected
	if (numCountries.includes(Number(country.id))) return false;
	if (numRegions.includes(Number(region.id))) return false;
	if (numStates.includes(Number(state.id))) return false;

	const cityIds = getCityIdsFromState(state);
	if (!cityIds || cityIds.length === 0) return false;

	let selectedCount = 0;
	for (const cId of cityIds) {
		if (
			isCitySelected(
				cId,
				state.id,
				region.id,
				country.id,
				numCities,
				numStates,
				numRegions,
				numCountries,
			)
		) {
			selectedCount++;
		}
	}

	// State is partial if it's not fully selected (checked at start)
	// AND at least one city is selected but not all
	return selectedCount > 0 && selectedCount < cityIds.length;
};

/**
 * Check if a country is partially selected (some but not all regions/states/cities selected)
 * Supports all hierarchy types:
 * 1. country -> regions -> states -> cities
 * 2. country -> states -> cities
 * 3. country -> cities
 */
export const isCountryPartiallySelected = (
	country: TCountry,
	selectedLocation: number[] = [],
	selectedCountryRegions: number[] = [],
	selectedRegionStates: number[] = [],
	selectedStateCities: number[] = [],
): boolean => {
	const numCountries = selectedLocation.map(Number);
	const numRegions = selectedCountryRegions.map(Number);
	const numStates = selectedRegionStates.map(Number);
	const numCities = selectedStateCities.map(Number);

	if (numCountries.includes(Number(country.id))) return false; // fully selected

	// Check regions -> states -> cities hierarchy
	if (country.regions && country.regions.length > 0) {
		for (const region of country.regions) {
			if (numRegions.includes(Number(region.id))) return true;
			if (region.states) {
				for (const state of region.states) {
					if (numStates.includes(Number(state.id))) return true;
					if (
						state.cities &&
						state.cities.some((c) => numCities.includes(Number(c.id)))
					) {
						return true;
					}
				}
			}
		}
	}

	// Check direct states -> cities hierarchy (country -> states -> cities)
	if (country.states && country.states.length > 0) {
		for (const state of country.states) {
			if (numStates.includes(Number(state.id))) return true;
			if (state.cities && state.cities.some((c) => numCities.includes(Number(c.id)))) {
				return true;
			}
		}
	}

	// Check direct cities hierarchy (country -> cities)
	if (country.cities && country.cities.length > 0) {
		if (country.cities.some((c) => numCities.includes(Number(c.id)))) {
			return true;
		}
	}

	return false;
};
